import { create } from 'zustand';
import {
  LeadEnrollmentQuestionnaireType,
  LeadType,
} from '@php-beam/packages/frontend/src/types/lead';
import { PusherEventData } from '@php-beam/packages/frontend/src/types/shared';
import { UpdateAppointmentType } from '@php-beam/packages/frontend/src/types/appointment';
import { UserType } from '@php-beam/packages/frontend/src/types/auth';
import { AppointmentType } from '@php-beam/packages/tps-ui/src/types/tps-types';
import { EnrollmentType } from '@php-beam/packages/enrollment-log/resources/Pages/support/types';

type ModalMode = 'prompt' | 'confirm' | 'loading';

export type UpcomingTpsAppointmentType = {
  lead: LeadType;
  user: UserType;
  appointment: AppointmentType;
  enrollment?: EnrollmentType;
  questionnaire?: LeadEnrollmentQuestionnaireType;
};

type SetModalParams<T extends ModalMode> = {
  modalMode: T;
  promptMsg: string;
  promptCallback?: (message: string | boolean) => void;
  defaultValue?: string;
  cancelBtnLabel?: string;
  confirmBtnLabel?: string;
  modalTitle?: string;
  implicitClose?: boolean;
  disableClose?: boolean;
  showModal?: boolean;
};

export type GlobalStore = {
  isLoading: boolean;
  _globalUi: {
    modal: {
      modalMode?: ModalMode;
      promptMsg: string;
      promptCallback?: (message: string | boolean) => void;
      showModal: boolean;
      cancelBtnLabel?: string;
      confirmBtnLabel?: string;
      defaultValue?: string;
      modalTitle?: string;
      implicitClose?: boolean;
      disableClose?: boolean;
    };

    leadCardDrawer: {
      showDrawer: boolean;
      lead?: LeadType;
    };

    appointmentBuilder: {
      showAppointmentBuilder: boolean;
      leadAppointment?: LeadType;
    };

    appointmentUpdater: {
      showAppointmentUpdater: boolean;
      appointment?: UpdateAppointmentType;
    };

    pusherEvent: {
      showPusherEvent: boolean;
      event?: PusherEventData;
    };

    upcomingTpsAppointment: {
      showUpcomingTpsAppointment: boolean;
      data?: Map<number, UpcomingTpsAppointmentType>;
    };
  };

  setModal: <T extends ModalMode | undefined>(
    params: T extends ModalMode ? SetModalParams<T> : false,
  ) => void;

  openLeadDrawer: (props: { lead?: LeadType; showDrawer?: boolean }) => void;

  openNewAppointment: (props: {
    leadAppointment?: LeadType;
    showAppointmentBuilder?: boolean;
  }) => void;

  setIsLoading: (value: boolean) => void;

  openAppointmentUpdater: (props: {
    appointment?: UpdateAppointmentType;
    showAppointmentUpdater?: boolean;
  }) => void;

  openPusherEvent: (props: {
    event?: PusherEventData;
    showPusherEvent?: boolean;
  }) => void;

  openUpComingTpsAppointment: (props: {
    data: UpcomingTpsAppointmentType;
    showUpcomingTpsAppointment?: boolean;
    action?: 'open' | 'close' | 'remove';
  }) => void;
};

const useGlobalStore = create<GlobalStore>((set, get) => ({
  _globalUi: {
    modal: {
      showModal: false,
      promptMsg: '',
      cancelBtnLabel: undefined,
      confirmBtnLabel: undefined,
    },
    leadCardDrawer: {
      showDrawer: false,
    },
    appointmentBuilder: {
      showAppointmentBuilder: false,
    },
    appointmentUpdater: {
      showAppointmentUpdater: false,
    },
    pusherEvent: {
      showPusherEvent: false,
    },
    upcomingTpsAppointment: {
      showUpcomingTpsAppointment: false,
    },
  },
  isLoading: false,

  setModal: (props) =>
    set({
      _globalUi: {
        ...get()._globalUi,
        modal:
          props === false
            ? {
                modalMode: undefined,
                promptMsg: '',
                promptCallback: undefined,
                showModal: false,
                defaultValue: undefined,
              }
            : { showModal: true, ...props },
      },
    }),
  setIsLoading: (value: boolean) =>
    set((state) => ({
      ...state,
      isLoading: value,
    })),

  openLeadDrawer: ({ lead, showDrawer }) =>
    set({
      _globalUi: {
        ...get()._globalUi,
        leadCardDrawer: {
          lead,
          showDrawer: showDrawer ?? true,
        },
      },
    }),

  openNewAppointment: ({ leadAppointment, showAppointmentBuilder }) =>
    set({
      _globalUi: {
        ...get()._globalUi,
        appointmentBuilder: {
          leadAppointment,
          showAppointmentBuilder: showAppointmentBuilder ?? true,
        },
      },
    }),

  openAppointmentUpdater: ({ appointment, showAppointmentUpdater }) =>
    set({
      _globalUi: {
        ...get()._globalUi,
        appointmentUpdater: {
          appointment,
          showAppointmentUpdater: showAppointmentUpdater ?? true,
        },
      },
    }),

  openPusherEvent: ({ event, showPusherEvent }) =>
    set({
      _globalUi: {
        ...get()._globalUi,
        pusherEvent: {
          event,
          showPusherEvent: showPusherEvent ?? true,
        },
      },
    }),

  openUpComingTpsAppointment: ({ data, action }) => {
    const currentData = get()._globalUi.upcomingTpsAppointment.data;

    let updatedData = currentData;

    switch (action) {
      case 'open':
        if (data) {
          if (currentData) {
            const existingEntry = Array.from(currentData.values()).find(
              (item) => item.appointment.id === data.appointment.id,
            );

            if (!existingEntry) {
              const key = currentData.size;
              updatedData = new Map(currentData);
              updatedData.set(key, data);
            } else {
              updatedData = currentData;
            }
          } else {
            updatedData = new Map().set(0, data);
          }
        }
        break;

      case 'close':
        updatedData = undefined;
        break;

      case 'remove':
        if (data && currentData && currentData.size > 0) {
          updatedData = new Map(currentData);

          // Find and remove the matching data
          for (const [key, value] of updatedData.entries()) {
            // You might need to adjust this comparison based on your data structure
            if (JSON.stringify(value) === JSON.stringify(data)) {
              updatedData.delete(key);
              break; // Remove first match only
            }
          }

          if (updatedData.size === 0) {
            updatedData = undefined;
          }
        }
        break;
    }

    let shouldShow = false;
    if (action === 'open') {
      shouldShow = !!(data && updatedData);
    } else if (action === 'close') {
      shouldShow = false;
    } else if (action === 'remove') {
      shouldShow = !!(updatedData && updatedData.size > 0);
    }

    set({
      _globalUi: {
        ...get()._globalUi,
        upcomingTpsAppointment: {
          showUpcomingTpsAppointment: shouldShow,
          data: updatedData,
        },
      },
    });
  },
}));

export default useGlobalStore;
